import { useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
	ClipboardDocumentIcon,
	CheckIcon,
	ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Modal, Button, AppLoader } from '../ui';
import { bookingLinkApi, buildWalkInBookingUrl, buildPaidBookingUrl, ApiError } from '../../api';
import type { ProviderLocation } from '../../types/provider';

export interface BookingLinkModalProps {
	isOpen: boolean;
	onClose: () => void;
	/** Locations the provider can generate links for */
	locations: ProviderLocation[];
	/** Location preselected when the modal opens (usually the active one) */
	selectedLocationId: number | null;
}

type LinkKey = 'walkIn' | 'walkInPaid';

const formatLocationLabel = (location: ProviderLocation): string => {
	const label = [location.locationName || location.address, location.city, location.state]
		.filter(Boolean)
		.join(', ');
	return location.primary ? `${label} ★` : label;
};

/**
 * navigator.clipboard is unavailable outside secure contexts (e.g. plain-http
 * LAN testing), so fall back to a hidden textarea + execCommand there.
 */
const copyText = async (text: string): Promise<boolean> => {
	try {
		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch {
		// fall through to the legacy path
	}

	try {
		const textarea = document.createElement('textarea');
		textarea.value = text;
		textarea.setAttribute('readonly', '');
		textarea.style.position = 'fixed';
		textarea.style.opacity = '0';
		document.body.appendChild(textarea);
		textarea.select();
		const ok = document.execCommand('copy');
		document.body.removeChild(textarea);
		return ok;
	} catch {
		return false;
	}
};

interface BookingLinkCardProps {
	title: string;
	description: string;
	url: string;
	accentClass: string;
	isCopied: boolean;
	onCopy: () => void;
}

const BookingLinkCard = ({
	title,
	description,
	url,
	accentClass,
	isCopied,
	onCopy,
}: BookingLinkCardProps) => (
	<div
		className={`rounded-xl border border-gray-200 dark:border-gray-700 border-l-4 ${accentClass} bg-gray-50 dark:bg-gray-700/40 px-4 py-3.5`}
	>
		<h4 className="text-sm font-bold text-gray-900 dark:text-white">{title}</h4>
		<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
		<div className="mt-3 flex items-center gap-2">
			<input
				type="text"
				readOnly
				value={url}
				onFocus={e => e.currentTarget.select()}
				className="flex-1 min-w-0 px-3 py-2 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:border-[#667eea]"
			/>
			<Button variant="secondary" size="sm" onClick={onCopy} className="shrink-0">
				{isCopied ? (
					<>
						<CheckIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
						Copied
					</>
				) : (
					<>
						<ClipboardDocumentIcon className="w-4 h-4" />
						Copy
					</>
				)}
			</Button>
		</div>
	</div>
);

const BookingLinkModal = ({
	isOpen,
	onClose,
	locations,
	selectedLocationId,
}: BookingLinkModalProps) => {
	const [locationId, setLocationId] = useState<number | null>(selectedLocationId);
	const [tokens, setTokens] = useState<{
		bookToken: string;
		bookWithPaymentToken: string;
	} | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [copiedKey, setCopiedKey] = useState<LinkKey | null>(null);

	const activeLocations = useMemo(() => locations.filter(loc => loc.active), [locations]);

	// Reset to the caller's location every time the modal opens
	useEffect(() => {
		if (isOpen) {
			setLocationId(selectedLocationId ?? activeLocations[0]?.id ?? null);
			setCopiedKey(null);
		}
	}, [isOpen, selectedLocationId, activeLocations]);

	const fetchTokens = useCallback(async () => {
		if (!locationId) return;
		setIsLoading(true);
		setError(null);
		setCopiedKey(null);
		try {
			const data = await bookingLinkApi.generate(locationId);
			setTokens(data);
		} catch (err) {
			setTokens(null);
			setError(
				err instanceof ApiError && err.message
					? err.message
					: 'Failed to generate booking links. Please try again.'
			);
		} finally {
			setIsLoading(false);
		}
	}, [locationId]);

	useEffect(() => {
		if (isOpen && locationId) {
			fetchTokens();
		}
	}, [isOpen, locationId, fetchTokens]);

	// Clear stale links when the modal closes so a reopen never flashes another
	// location's URLs before the refetch lands.
	useEffect(() => {
		if (!isOpen) {
			setTokens(null);
			setError(null);
		}
	}, [isOpen]);

	const handleCopy = async (key: LinkKey, url: string) => {
		const ok = await copyText(url);
		if (ok) {
			setCopiedKey(key);
			toast.success('Link copied to clipboard');
			setTimeout(() => setCopiedKey(current => (current === key ? null : current)), 2000);
		} else {
			toast.error('Could not copy the link. Please copy it manually.');
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} title={'\u{1F517} Public Booking Links'} size="md">
			<p className="text-sm text-gray-500 dark:text-gray-400">
				Share these links so customers can book with you directly. Pick which location the
				bookings should go to.
			</p>

			{/* Location selector */}
			<div className="provider-form__group mt-4 mb-0">
				<label className="provider-form__label" htmlFor="bookingLinkLocation">
					Location
				</label>
				<select
					id="bookingLinkLocation"
					className="provider-form__input"
					value={locationId ?? ''}
					onChange={e => setLocationId(e.target.value ? Number(e.target.value) : null)}
					disabled={activeLocations.length === 0}
				>
					{activeLocations.length === 0 && <option value="">No active locations</option>}
					{activeLocations.map(loc => (
						<option key={loc.id} value={loc.id}>
							{formatLocationLabel(loc)}
						</option>
					))}
				</select>
			</div>

			{/* Links */}
			<div className="mt-4 space-y-3">
				{isLoading ? (
					<div className="flex items-center justify-center py-10">
						<AppLoader message="Generating links" variant="inline" />
					</div>
				) : error ? (
					<div className="flex flex-col items-center gap-3 py-8 text-center">
						<ExclamationTriangleIcon className="w-8 h-8 text-amber-500" />
						<p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
						<Button variant="secondary" size="sm" onClick={fetchTokens}>
							Try Again
						</Button>
					</div>
				) : tokens ? (
					<>
						<BookingLinkCard
							title="Book without payment"
							description="Customers check in as a walk-in — no deposit, served in order of arrival."
							url={buildWalkInBookingUrl(tokens.bookToken)}
							accentClass="!border-l-[#667eea]"
							isCopied={copiedKey === 'walkIn'}
							onCopy={() =>
								handleCopy('walkIn', buildWalkInBookingUrl(tokens.bookToken))
							}
						/>
						<BookingLinkCard
							title="Book with payment"
							description="Customers pay a deposit, can pick a staff member, and reserve their spot."
							url={buildPaidBookingUrl(tokens.bookWithPaymentToken)}
							accentClass="!border-l-[#764ba2]"
							isCopied={copiedKey === 'walkInPaid'}
							onCopy={() =>
								handleCopy(
									'walkInPaid',
									buildPaidBookingUrl(tokens.bookWithPaymentToken)
								)
							}
						/>
					</>
				) : (
					<p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
						Select a location to generate booking links.
					</p>
				)}
			</div>

			<div className="mt-6 flex justify-start">
				<Button variant="secondary" onClick={onClose}>
					Close
				</Button>
			</div>
		</Modal>
	);
};

export default BookingLinkModal;
